import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import apiService from '../../services/apiService';
import { clearAuthedFileCache } from '../../utils/authedFile';
import { tenantId } from '../../config/tenant';

export const STORAGE = {
  TOKEN:  'AuthKey',    
  //LOG_ID: 'dge_log_id',
  USER:   'ADU_EmployeeId',
};

export const loginThunk = createAsyncThunk(
  'auth/login',
  async ({ serviceNo, password }, { rejectWithValue }) => {
    try {
      const data = await apiService.post(
        `/Users/Login?p_username=${encodeURIComponent(serviceNo)}&p_passwordHash=${encodeURIComponent(password)}&p_clientCode=${tenantId}`
      );
      if (data.StatusCode !== 200) {
        return rejectWithValue(data.Message || 'Invalid credentials. Please try again.');
      }

      const userData = data.UserDetails || data.ResultSet || data || {};
      const token = data.Token || data.AuthKey || userData.AuthKey || '';
      const logId = data.LogId || '';

      const user = {
        userId: userData.ADU_UserId || '',
        serviceNo:  userData.ServiceNo || userData.ADU_EmployeeId || serviceNo,
        phone:      userData.PhoneNo || '',
        firstName:  userData.FName || userData.ADU_Username || 'User',
        lastName:   userData.LName || '',

        name:       `${userData.FName || userData.ADU_Username || ''} ${userData.LName || ''}`.trim() || 'User',
        avatar:     `${(userData.FName || userData.ADU_Username || 'U')[0].toUpperCase()}${(userData.LName || '')?.[0]?.toUpperCase() ?? ''}`,

        raw:        userData,
      };

      // ── Persist to localStorage ────────────────────────────────────────────
      localStorage.setItem(STORAGE.TOKEN,  token);
      localStorage.setItem(STORAGE.LOG_ID, logId);
      localStorage.setItem(STORAGE.USER,   JSON.stringify(user));

      return { token, logId, user };
    } catch (err) {
      // Network errors, CORS, parse failures, etc.
      return rejectWithValue(err.message || 'Connection error. Please check your network.');
    }
  }
);

export const logoutThunk = createAsyncThunk(
  'auth/logout',
  async (_, { getState }) => {
    const { token } = getState().auth;
    try {
      if (token) {
        await apiService.post('/Login/Logout', {}, token);
      }
    } catch {

    }
    localStorage.removeItem(STORAGE.TOKEN);
    localStorage.removeItem(STORAGE.LOG_ID);
    localStorage.removeItem(STORAGE.USER);

    clearAuthedFileCache();
  }
);


export const restoreSessionThunk = createAsyncThunk(
  'auth/restoreSession',
  async (_, { rejectWithValue }) => {
    const token = localStorage.getItem(STORAGE.TOKEN);
    const logId = localStorage.getItem(STORAGE.LOG_ID);
    const raw   = localStorage.getItem(STORAGE.USER);

    if (!token || !raw) return rejectWithValue('No saved session');

    try {
      const user = JSON.parse(raw);
      return { token, logId, user };
    } catch {
      // Corrupted storage — clear it
      localStorage.removeItem(STORAGE.TOKEN);
      localStorage.removeItem(STORAGE.LOG_ID);
      localStorage.removeItem(STORAGE.USER);
      return rejectWithValue('Corrupted session data');
    }
  }
);

// ─────────────────────────────────────────────────────────────────────────────
// Slice
// ─────────────────────────────────────────────────────────────────────────────
const initialState = {
  user:            null,
  token:           null,
  logId:           null,
  isAuthenticated: false,
  loading:         false,
  error:           null,
};

const authSlice = createSlice({
  name: 'auth',
  initialState,


  reducers: {

    clearError(state) {
      state.error = null;
    },

    resetAuth() {
      return initialState;
    },
  },

  // ── Async action handlers ─────────────────────────────────────────────────
  extraReducers: (builder) => {
    // ── loginThunk ────────────────────────────────────────────────────────────
    builder
      .addCase(loginThunk.pending, (state) => {
        state.loading = true;
        state.error   = null;
      })
      .addCase(loginThunk.fulfilled, (state, action) => {
        state.loading         = false;
        state.isAuthenticated = true;
        state.token           = action.payload.token;
        state.logId           = action.payload.logId;
        state.user            = action.payload.user;
        state.error           = null;
      })
      .addCase(loginThunk.rejected, (state, action) => {
        state.loading = false;
        state.error   = action.payload ?? 'Login failed';
      });

    // ── logoutThunk ───────────────────────────────────────────────────────────
    builder
      .addCase(logoutThunk.fulfilled, () => initialState)
      .addCase(logoutThunk.rejected,  () => initialState); // clear even on error

    // ── restoreSessionThunk ───────────────────────────────────────────────────
    builder
      .addCase(restoreSessionThunk.pending, (state) => {
        // Don't show a spinner for session restore — app boots with loading:false
        state.loading = false;
      })
      .addCase(restoreSessionThunk.fulfilled, (state, action) => {
        state.isAuthenticated = true;
        state.token           = action.payload.token;
        state.logId           = action.payload.logId;
        state.user            = action.payload.user;
      })
      .addCase(restoreSessionThunk.rejected, (state) => {
        Object.assign(state, initialState);
      });
  },
});

export const { clearError, resetAuth } = authSlice.actions;
export default authSlice.reducer;